import React, { useState, useRef, useEffect } from "react";

interface LogoProps {
  className?: string;
  alt?: string;
}

/**
 * Renders the Give Protocol brand logo, falling back to a "GP" text mark when the SVG fails to load.
 * @param props - Component props.
 * @param props.className - Optional class names applied to the rendered element.
 * @param props.alt - Alt text for the image. Pass an empty string when adjacent text already names the brand.
 * @returns The logo image, or a text fallback if the asset cannot be loaded.
 */
export const Logo: React.FC<LogoProps> = ({
  className,
  alt = "Give Protocol",
}) => {
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;

    /** Marks the logo as failed so the text fallback is rendered. */
    const handleError = () => setError(true);
    img.addEventListener("error", handleError);

    return () => {
      img.removeEventListener("error", handleError);
    };
  }, []);

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <span className="text-2xl font-bold">GP</span>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src="/give_logo_gradient.svg"
      alt={alt}
      className={className}
      width={32}
      height={32}
    />
  );
};
